// src/Components/ContactDialog/ContactDialog.js

import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { createTheme, ThemeProvider } from '@mui/material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';
import LogoImage from '../../assets/images/logo.png';

const theme = createTheme();

export default function ContactDialog({ open, onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' }); // type: 'success' | 'error'

  const handleClose = () => {
    setFormData({ name: '', email: '', message: '' });
    setSubmitStatus({ type: '', message: '' });
    setIsSubmitting(false);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Formatted explicitly for the 'Trigger Email from Firestore' Extension
      await addDoc(collection(db, 'contactMessages'), {
        to: ['info@loktr.net'],
        message: {
          subject: `New loKtr Contact Form Submission from ${formData.name}`,
          text: `Name: ${formData.name}\nEmail: ${formData.email}\nMessage:\n${formData.message}`,
          html: `
            <h3>New Message from loKtr Contact Form</h3>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Message:</strong><br/>${formData.message.replace(/\n/g, '<br/>')}</p>
          `,
          replyTo: formData.email,
        },
        rawInput: {
          name: formData.name,
          email: formData.email,
          message: formData.message,
          createdAt: serverTimestamp(),
        },
      });

      setSubmitStatus({
        type: 'success',
        message: 'Your message has been sent successfully. We will be in touch shortly.',
      });

      // Auto-close after 3 seconds on success
      setTimeout(() => {
        if (open) {
          handleClose();
        }
      }, 3000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'There was an error sending your message. Please try again later.',
      });
      setIsSubmitting(false);
    }
  };

  // Simplified styles imported directly from Signup.js
  const inputStyle = {
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputLabel-root': { transform: 'translate(14px, 10px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' },
  };

  // Multiline fields cannot have a fixed 40px height, so we only align the label transformations
  const multilineInputStyle = {
    '& .MuiInputLabel-root': { transform: 'translate(14px, 10px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' },
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={!isSubmitting ? handleClose : undefined}
        maxWidth='xs'
        fullWidth
        sx={{ zIndex: 100015 }} // Forces dialog into the Global System Overlays tier
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}>
          <Typography component='span' variant='h6'>
            Contact loKtr
          </Typography>
          <img
            src={LogoImage}
            alt='loKtr Logo'
            style={{ maxHeight: '40px', maxWidth: '120px', objectFit: 'contain' }}
          />
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {submitStatus.message && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: submitStatus.type === 'success' ? '#e8f5e9' : '#ffebee',
                  borderRadius: 1,
                }}>
                <Typography
                  variant='body2'
                  color={submitStatus.type === 'success' ? 'success.main' : 'error.main'}>
                  {submitStatus.message}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                size='small'
                margin='dense'
                label='Name'
                name='name'
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                autoFocus
                disabled={isSubmitting || submitStatus.type === 'success'}
                sx={inputStyle}
              />
              <TextField
                size='small'
                margin='dense'
                label='Email Address'
                name='email'
                type='email'
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                disabled={isSubmitting || submitStatus.type === 'success'}
                sx={inputStyle}
              />
              <TextField
                size='small'
                margin='dense'
                label='Message'
                name='message'
                value={formData.message}
                onChange={handleChange}
                required
                fullWidth
                multiline
                rows={4}
                disabled={isSubmitting || submitStatus.type === 'success'}
                sx={multilineInputStyle}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={isSubmitting} color='inherit'>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={isSubmitting || submitStatus.type === 'success'}
              sx={{
                minWidth: 100,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.02)',
                },
              }}>
              {isSubmitting ? <CircularProgress size={24} color='inherit' /> : 'Send'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
}
